import { StructuredDataService } from "@brookbush/structured-data";
import type { LandingPageContent } from "@brookbush/types";

import clsx from "clsx";
import type { GetStaticProps } from "next/types";
import { NextSeo } from "next-seo";
import type { PropsWithChildren } from "react";

import CptProgramsHero from "./components/cpt-programs/CptProgramsHero";
import CptStudyCompanion from "./components/cpt-programs/CptStudyCompanion";
import CptTwoWays from "./components/cpt-programs/CptTwoWays";
import CptWhyBrookbush from "./components/cpt-programs/CptWhyBrookbush";
import LandingPageAccreditations from "./components/LandingPageAccreditations";
import LandingPageArticles from "./components/LandingPageArticles";
import LandingPageBody from "./components/LandingPageBody";
import LandingPageFeatures from "./components/LandingPageFeatures";
import LandingPageModules from "./components/LandingPageModules";
import LandingPagePopularCourses from "./components/LandingPagePopularCourses";
import LandingPageTestimonials from "./components/LandingPageTestimonials";
import ProgramsHero from "./components/ProgramsHero";
import Faqs from "~/components/Faqs";
import Footer from "~/components/Footer";
import FullWidthCTA from "~/components/FullWidthCTA";
import Hero from "~/components/Hero";
import LandingPagePartners from "~/components/LandingPagePartners";
import Navbar from "~/components/Navbar";
import {
	CPT_PROGRAMS_PAGE,
	getCptProgramsFaqs,
	getCptProgramsFeatures,
	isCptProgramsPageSlug,
} from "~/lib/cpt-programs-page";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

const LandingPage: NextPageWithProps<{
	landingPageContent: LandingPageContent;
	structuredData: string;
}> = ({ landingPageContent, structuredData }) => {
	const isProgramsPage = landingPageContent.slug === "programs";
	const isCptProgramsPage = isCptProgramsPageSlug(landingPageContent.slug);

	const seoTitle = isCptProgramsPage
		? CPT_PROGRAMS_PAGE.seoTitle
		: landingPageContent.title;
	const seoDescription = isCptProgramsPage
		? CPT_PROGRAMS_PAGE.seoDescription
		: landingPageContent.description;

	const features = isCptProgramsPage
		? getCptProgramsFeatures(landingPageContent.features ?? [])
		: landingPageContent.features;

	const faqs = isCptProgramsPage
		? getCptProgramsFaqs(landingPageContent.faqs ?? [])
		: landingPageContent.faqs;

	return (
		<>
			<NextSeo
				title={seoTitle}
				description={seoDescription}
				canonical={`${process.env.NEXT_PUBLIC_SITE_URL}/info/${landingPageContent.slug}`}
				openGraph={{
					title: seoTitle,
					url: `${process.env.NEXT_PUBLIC_SITE_URL}/info/${landingPageContent.slug}`,
					description: seoDescription,
					images: [
						{
							url: landingPageContent.thumbnail.url,
							width: 800,
							height: 600,
							alt: seoTitle,
						},
					],
				}}
			/>

			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>

			<main
				className={clsx({ "-mt-16": landingPageContent.slug !== "programs" })}
			>
				<Navbar isHomePage={landingPageContent.slug !== "programs"} />

				{isCptProgramsPage ? (
					<CptProgramsHero />
				) : landingPageContent.slug === "programs" ? (
					<ProgramsHero />
				) : (
					<Hero
						title={landingPageContent.hero.title}
						subtitle={landingPageContent.hero.description}
						imageUrl={landingPageContent.thumbnail.url}
						link={
							landingPageContent.linkedCertification && {
								href: `/certifications/${landingPageContent.linkedCertification.slug}`,
								label: landingPageContent.linkedCertification.title,
							}
						}
					/>
				)}

				{isProgramsPage ? (
					<>
						<LandingPageFeatures features={features} />

						<LandingPagePartners
							partners={landingPageContent.partners}
							customers={landingPageContent.customers}
						/>
					</>
				) : (
					<>
						<LandingPagePartners
							partners={landingPageContent.partners}
							customers={landingPageContent.customers}
						/>

						<LandingPageFeatures features={features} />
					</>
				)}

				{isCptProgramsPage ? (
					<>
						<CptTwoWays />
						<CptStudyCompanion />
						<CptWhyBrookbush />
						{faqs && <Faqs faqs={faqs} />}
						<LandingPageAccreditations
							accreditations={landingPageContent.accreditations}
							showNccaCptEntry
						/>
					</>
				) : (
					<>
						<LandingPageBody body={landingPageContent.body} />
						<LandingPageAccreditations
							accreditations={landingPageContent.accreditations}
						/>
					</>
				)}

				{landingPageContent.linkedCertification && (
					<LandingPageModules
						certification={landingPageContent.linkedCertification as any}
					/>
				)}

				<LandingPageTestimonials
					testimonials={landingPageContent.testimonials}
				/>

				<LandingPagePopularCourses
					courses={landingPageContent.popularCourses}
				/>

				<LandingPageArticles articles={landingPageContent.articleResources} />

				{!isCptProgramsPage && faqs && <Faqs faqs={faqs} />}

				<FullWidthCTA
					footerSentence={
						isCptProgramsPage ? CPT_PROGRAMS_PAGE.footerCtaSentence : undefined
					}
				/>

				<Footer />
			</main>
		</>
	);
};

// eslint-disable-next-line react/display-name
LandingPage.Layout = ({ children }: PropsWithChildren<{}>) => <>{children}</>;

export const getStaticPaths = async () => {
	return {
		paths: [],
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps = async (req) => {
	const slug = req.params?.slug as string;

	try {
		const landingPageContent: LandingPageContent = await fetcher(
			`/landing-pages/${slug}`,
		);

		if (!landingPageContent) {
			return {
				notFound: true,
			};
		}

		const structuredData = await Promise.all([
			StructuredDataService.generateLandingPage(landingPageContent),
			StructuredDataService.generateLandingPageProduct(landingPageContent),
			StructuredDataService.getBreadcrumbs(`/info/${slug}`, (fragment) =>
				fragment === slug ? landingPageContent.title : undefined,
			),
			...(landingPageContent.faqs
				? [StructuredDataService.generateFAQs(landingPageContent.faqs)]
				: []),
		]);

		for (const result of structuredData) {
			if (!result) {
				return {
					notFound: true,
				};
			}
		}

		return {
			props: {
				landingPageContent,
				structuredData: StructuredDataService.withContext({
					"@graph": structuredData,
				}),
			},
		};
	} catch (_error) {
		return {
			props: {
				error: "Failed to load data",
			},
		};
	}
};

export default LandingPage;
